import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { Box, Skeleton } from '@mui/material';

import {
  PayrollCalculationTotals,
  PayrollPayslipList,
  PayrollStatus,
  PayrollSummary,
  PayrollType,
  SearchFilteringRange,
  SearchSorting,
  WithMetadataResult,
  fetchSearchAllPayrolls,
  fetchSearchAllPayslips,
} from '@octopus/api';
import { formatDateBR, formatMoney } from '@octopus/formatters';
import { summaryElements } from '@octopus/payroll-engine/public-types/core';
import {
  DataGrid,
  FilterOptions,
  GridColDef,
  GridValueGetterParams,
  makeDateRangeFilter,
  makeMoneyRangeFilter,
  useDataGrid,
} from '@octopus/ui/data-grid';

import { BASE_COLUMN_WIDTH } from '../../../modules/components/table/columns/Constants';
import {
  approvalDateColumn,
  periodColumn,
  reviewActionColumn,
} from '../../../modules/components/table/columns/PayrollColumns';
import { workerNameColumn } from '../../../modules/components/table/columns/WorkerColumns';
import { useLegalEntityFilter } from '../../../modules/components/table/filters/CompanyFilter';
import { usePeriodFilter } from '../../../modules/components/table/filters/PayrollFilter';
import { DataFetching } from '../../../modules/dataFetching';
import { PeriodFormat } from '../../../modules/types';
import { getActiveElementFilters } from '../../../utils';
import { payrollRPAReceiptsColumn } from '../components/Columns';

type PayrollPayslipListWithMetadata = WithMetadataResult & PayrollPayslipList;

export function RpaTable({
  organizationId,
  companyId,
  type,
  compareTo,
  onWorkerClick,
  selectedStatus,
  dataGridProps,
  setCountByStatus,
}: {
  organizationId: string;
  companyId: string;
  type: PayrollType;
  compareTo: PeriodFormat | undefined;
  onWorkerClick: (
    payrollId: string,
    contractId: string,
    rows: Array<PayrollSummary>,
    selectedTab: PayrollStatus,
  ) => void;
  selectedStatus: PayrollStatus;
  dataGridProps: ReturnType<typeof useDataGrid>;
  setCountByStatus: (countByStatus: {
    open: number;
    approved: number;
    closed: number;
    payslipApprovedNotSent: number;
  }) => void;
}) {
  const { sortingProps, filteringProps, searchProps, paginationProps } =
    dataGridProps;

  const useFetch = () => {
    // Get calculation total range filters
    const calculationRangeFilters = new Set(
      Object.values(summaryElements).map(
        (element) => `calculationTotals.${element}`,
      ) as string[],
    );

    // Process calculation total range filters
    const calculationRangeFiltersValue = Object.fromEntries(
      Object.entries(filteringProps.filtersState.rangeFilters)
        .filter(([k]) => filteringProps.activeFilters.has(k))
        .map(([k, v]) => [`calculationTotals.${k}`, v] as const)
        .filter(([k]) => calculationRangeFilters.has(k)),
    ) as Record<string, SearchFilteringRange>;

    // Process other range filters (like paymentDate)
    const otherRangeFilters = Object.fromEntries(
      Object.entries(filteringProps.filtersState.rangeFilters)
        .filter(([k]) => filteringProps.activeFilters.has(k))
        .filter(
          ([k]) => !calculationRangeFilters.has(`calculationTotals.${k}`),
        ),
    );

    const [query] = (() => {
      const { searchTerm } = searchProps;
      return [searchTerm, ''];
    })();

    const elementFilters = {
      type: [type],
      status:
        selectedStatus === 'closed'
          ? ['reconciled', selectedStatus]
          : [selectedStatus],
    };

    const arrayFilters = getActiveElementFilters(filteringProps);
    const payrollBody = {
      query: query.length > 0 ? query : undefined,
      pagination: {
        size: paginationProps.rowsPerPage,
        page: paginationProps.page,
      },
      ...(sortingProps.field
        ? {
            sorting: [
              {
                field: prepareSortField(sortingProps.field),
                order: sortingProps.order,
              },
            ] as SearchSorting,
          }
        : {
            sorting: [
              {
                field: 'period',
                order: 'asc',
              },
            ] as SearchSorting,
          }),
      filtering: {
        ranges: {
          ...calculationRangeFiltersValue,
          ...otherRangeFilters,
        },
        elements: elementFilters,
        arrays: arrayFilters,
      },
    };
    return useQuery({
      queryKey: [
        'searchAllRPAs',
        organizationId,
        companyId,
        query,
        paginationProps,
        sortingProps,
        calculationRangeFiltersValue,
        otherRangeFilters,
        elementFilters,
        arrayFilters,
        compareTo,
      ],
      queryFn: async () => {
        const payrolls = await fetchSearchAllPayrolls({
          pathParams: {
            organizationId,
            companyId,
          },
          body: payrollBody,
        });
        let payslips: PayrollPayslipListWithMetadata = {
          data: [],
          total: 0,
          size: 0,
          page: 0,
        };
        const payslipBody = {
          filtering: {
            // ranges: calculationRangeFiltersValue,
            elements: {
              type: [type],
              status: ['approved'],
              payrollId: payrolls.data.map((payroll) => payroll.payrollId),
            },
          },
        };

        payslips = await fetchSearchAllPayslips({
          pathParams: {
            organizationId,
            companyId,
          },
          body: payslipBody,
        });

        const comparisons: Record<string, PayrollCalculationTotals> = {};
        if (compareTo) {
          const fetchComparisonPayrollsResult = await fetchSearchAllPayrolls({
            pathParams: {
              organizationId,
              companyId,
            },
            body: {
              ...payrollBody,
              filtering: {
                elements: {
                  ...elementFilters,
                  period: [compareTo],
                  contractId: payrolls.data.map(
                    (payroll) => payroll.contractId,
                  ),
                },
                arrays: arrayFilters,
              },
              pagination: {
                size: 100,
              },
            },
          });
          for (const payroll of fetchComparisonPayrollsResult.data) {
            if (payroll.calculationTotals) {
              comparisons[payroll.contractId] = payroll.calculationTotals;
            }
          }
        }
        const payslipLookup = new Set(
          payslips.data.map((payslip) => payslip.payrollId),
        );
        const data = {
          ...payrolls,
          data: payrolls.data.map((payroll) => {
            return {
              ...payroll,
              ...(compareTo && {
                comparison: comparisons[payroll.contractId],
              }),
              ...{
                hasApprovedPayslip: payslipLookup.has(payroll.payrollId),
              },
            };
          }),
        };
        const typeStatusBuckets = data?.metadata?.buckets?.counters?.byProp?.[
          'type-status'
        ] as { [key: string]: any };
        const payslipsStatusByPeriod =
          payslips?.metadata?.buckets?.counters?.byProp?.[
            'type-period-status'
          ]?.['rpa'] ?? {};
        const payslipApprovedNotSent = Object.values(
          payslipsStatusByPeriod,
        ).reduce<number>((total, periodStatus) => {
          return (
            total + ((periodStatus as Record<string, number>)['approved'] ?? 0)
          );
        }, 0);
        const counters = typeStatusBuckets
          ? {
              open: typeStatusBuckets.rpa?.open ?? 0,
              approved: typeStatusBuckets.rpa?.approved ?? 0,
              closed:
                (typeStatusBuckets.rpa?.reconciled ?? 0) +
                (typeStatusBuckets.rpa?.closed ?? 0),
              payslipApprovedNotSent: payslipApprovedNotSent,
            }
          : undefined;

        return { data, counters };
      },
    });
  };

  const { data: response } = useFetch();

  useMemo((): void => {
    if (response?.counters) {
      setCountByStatus(response.counters);
    }
  }, [response?.counters, setCountByStatus]);

  return (
    <DataFetching
      useHook={useFetch}
      Loading={() => (
        <Box display="flex" flexDirection="column" gap="8px" pt={1}>
          <Skeleton variant="rounded" height={300} width="100%" />
        </Box>
      )}
      Data={({ data: response }) => {
        return response ? (
          <DataGrid<PayrollSummary>
            sortingProps={sortingProps}
            paginationProps={paginationProps}
            totalRowCount={response.data.total}
            getRowId={(row) => `${row.contractId}/${row.payrollId}`}
            rows={response.data.data}
            columns={getColumnsForStatus(selectedStatus)}
            onRowClick={(params) =>
              onRowClick({
                onWorkerClick,
                row: params.row,
                rows: response.data.data,
              })
            }
            emptyMessage={getEmptyMessageForStatus(selectedStatus)}
            getRowSx={() => ({
              height: '56px',
              'td:nth-last-of-type': {
                opacity: 0,
              },
              '&:hover': {
                'td:nth-last-of-type': {
                  opacity: 1,
                },
              },
            })}
          />
        ) : null;
      }}
    />
  );
}

function onRowClick({
  onWorkerClick,
  row,
  rows,
}: {
  row: PayrollSummary;
  onWorkerClick: (
    payrollId: string,
    contractId: string,
    rows: Array<PayrollSummary>,
    status: PayrollStatus,
  ) => void;
  rows: Array<PayrollSummary>;
}) {
  if (!row) {
    return;
  }

  onWorkerClick(row.payrollId, row.contractId, rows, row.status);
}

export function useFilters(): FilterOptions {
  return [
    usePeriodFilter(),
    makeMoneyRangeFilter({
      label: 'Valor',
      propertyToFilter: summaryElements.workerEarningsTotal,
      getRangeMin: () => 0,
      getRangeMax: () => 15_000,
    }),
    makeDateRangeFilter({
      label: 'Data de pagamento',
      propertyToFilter: 'paymentDate',
    }),
    useLegalEntityFilter(),
  ].filter(Boolean);
}

const columns: GridColDef<PayrollSummary>[] = [
  workerNameColumn({
    headerName: 'Autônomo',
  }),
  periodColumn(),
  {
    field: 'calculationTotals.workerEarningsTotal',
    headerName: 'Valor',
    sortable: true,
    flex: 0,
    width: BASE_COLUMN_WIDTH,
    maxWidth: BASE_COLUMN_WIDTH,
    valueGetter: (params: GridValueGetterParams) => {
      return formatMoney(params.row.calculationTotals.workerEarningsTotal);
    },
  },
  {
    field: 'paymentDate',
    headerName: 'Data de pagamento',
    sortable: true,
    flex: 0,
    width: BASE_COLUMN_WIDTH,
    maxWidth: BASE_COLUMN_WIDTH,
    valueGetter: (params: GridValueGetterParams) => {
      return formatDateBR(params.row.paymentDate || params.row.date);
    },
  },
].filter(Boolean);

function prepareSortField(orderBy?: string) {
  if (orderBy === 'name') {
    return 'workerData.name';
  }
  return orderBy;
}

function getColumnsForStatus(state: PayrollStatus) {
  switch (state) {
    case 'open':
      return [...columns, approvalDateColumn(), reviewActionColumn()];
    case 'approved':
    case 'closed':
      return [...columns, payrollRPAReceiptsColumn()];
    default:
      return columns;
  }
}

function getEmptyMessageForStatus(state: PayrollStatus) {
  switch (state) {
    case 'open':
      return 'Não existem pagamentos em aberto.';
    case 'approved':
      return 'Não existem pagamentos aprovados.';
    case 'closed':
      return 'Não existem pagamentos fechados.';
    default:
      return 'Não há pagamentos.';
  }
}
